/*
  Carrusel de la tarjeta: montar las fotos recien cuando hacen falta
  ------------------------------------------------------------
  `snippets/product-card.liquid` imprime la primera foto normal y las demas con
  `data-src` / `data-srcset` / `data-sizes` en vez de los atributos reales. Este
  archivo se los devuelve la primera vez que alguien se acerca a la tarjeta.

  Por que no alcanzaba `loading="lazy"`: los slides que no estan a la vista viven
  desplazados a la derecha dentro de un `overflow: hidden`, pero su caja sigue
  cerca del viewport, asi que el navegador los considera "por ver" y los baja
  igual. En /collections/mujer eso eran 105 fotos pedidas para mostrar 24.

  Los disparadores cubren los tres caminos de entrada — mouse, dedo y teclado — y
  ademas hay un `IntersectionObserver` de respaldo con `rootMargin` generoso, para
  que una tarjeta que ya esta en pantalla no dependa de que la toquen: sin eso,
  arrastrar el carrusel mostraria un slide vacio por un frame.
*/
(() => {
  const ATTRS = [
    ['data-src', 'src'],
    ['data-srcset', 'srcset'],
    ['data-sizes', 'sizes']
  ];

  const hydrate = (scope) => {
    const imgs = scope.querySelectorAll('img[data-ig-deferred-media]');
    if (!imgs.length) return;
    imgs.forEach((img) => {
      ATTRS.forEach(([from, to]) => {
        const value = img.getAttribute(from);
        if (value === null) return;
        img.setAttribute(to, value);
        img.removeAttribute(from);
      });
      img.removeAttribute('data-ig-deferred-media');
    });
  };

  const bind = (card) => {
    if (card.dataset.igMediaBound === '1') return;
    card.dataset.igMediaBound = '1';

    const run = () => hydrate(card);
    /* `once` alcanza porque `hydrate` es idempotente y el primer disparo ya deja
       la tarjeta entera montada. */
    ['pointerenter', 'touchstart', 'focusin'].forEach((type) =>
      card.addEventListener(type, run, { once: true, passive: true })
    );
  };

  const observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              hydrate(entry.target);
              observer.unobserve(entry.target);
            });
          },
          { rootMargin: '200px 0px' }
        )
      : null;

  const init = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    const cards = Array.from(scope.querySelectorAll('.card-wrapper'));
    if (scope.matches && scope.matches('.card-wrapper')) cards.push(scope);
    cards.forEach((card) => {
      if (!card.querySelector('img[data-ig-deferred-media]')) return;
      bind(card);
      if (observer) observer.observe(card);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => init(e && e.target));

  /* Red de contencion para las tarjetas que llegan por fetch sin avisar.
     `<product-recommendations>` (global.js, base Madrid) hace
     `this.innerHTML = …` y no llama a nadie, asi que sus tarjetas nunca se
     registraban: la primera foto se veia y las demas quedaban sin `src` — o sea
     slides rotos al avanzar. Lo mismo vale para cualquier otro inyector del tema
     (quick view, vistos recientemente, …). El filtro es barato: solo mira los
     nodos agregados, y `init` sale enseguida si no traen fotos diferidas. */
  if ('MutationObserver' in window) {
    new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches('img[data-ig-deferred-media]') || node.querySelector('img[data-ig-deferred-media]')) {
            init(node.closest('.card-wrapper') || node);
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* Lo consumen las grillas que inyectan tarjetas por fetch (favoritos, load more),
     igual que `loadeMoreSlider_G`. Los recomendados los cubre el observer. */
  window.igHydrateCardMedia = init;
})();
